import React, { useState, useRef, useEffect } from 'react';
import './Sidebar.css';

function Sidebar({ view, setView, savedLists = [], activeListId, onSelectList, onDeleteList, notebooks = [], activeNotebookId, onSelectNotebook, onNewNotebook, showStrategy, groups = [], activeGroupId, onSelectGroup, onDeleteGroup, showSettings, onShowSettings, onAddPerson, onCreateGroup, donors = [], onSelectPerson }) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [deleteConfirmList, setDeleteConfirmList] = useState(null);
  const [deleteConfirmGroup, setDeleteConfirmGroup] = useState(null);
  const groupsSectionExpanded = true; // Always expanded
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const sidebarRef = useRef(null);
  const hoverZoneRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter donors based on search query
  const filteredDonors = searchQuery.trim()
    ? donors.filter(d => d.name.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 5)
    : [];

  // Check if exact match exists
  const exactMatch = donors.some(d => d.name.toLowerCase() === searchQuery.trim().toLowerCase());
  const showAddOption = searchQuery.trim().length > 0 && !exactMatch;

  // Track window resize for desktop/mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Desktop hover handlers
  const handleMouseEnter = () => {
    if (isDesktop) {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (isDesktop) {
      setIsOpen(false);
    }
  };

  const handleListSelect = (listId, groupId = null) => {
    if (onSelectList) {
      onSelectList(listId);
    }
    // If the list belongs to a group, select the group (which sets view to 'people')
    // Otherwise, switch to lists view
    if (groupId) {
      if (onSelectGroup) onSelectGroup(groupId);
    } else {
      setView('lists');
    }
    // Close sidebar on mobile after selection
    if (window.innerWidth <= 768) {
      setIsOpen(false);
    }
  };

  const handleGroupSelect = (groupId) => {
    // Always expand when clicking group name, and navigate
    setExpandedGroupId(groupId);
    if (onSelectGroup) {
      onSelectGroup(groupId);
    }
    // Close sidebar on mobile after selection
    if (window.innerWidth <= 768) {
      setIsOpen(false);
    }
  };

  // Close sidebar on mobile when navigating
  const handleNavClick = (viewName) => {
    setView(viewName);
    if (window.innerWidth <= 768) {
      setIsOpen(false);
    }
  };

  const handleCreateGroupClick = (e) => {
    e.stopPropagation();
    if (onCreateGroup) {
      onCreateGroup();
    }
    if (window.innerWidth <= 768) {
      setIsOpen(false);
    }
  };

  const handleSearchSelect = (donor) => {
    if (onSelectPerson) {
      onSelectPerson(donor);
    }
    setSearchQuery('');
    setSearchFocused(false);
    if (window.innerWidth <= 768) {
      setIsOpen(false);
    }
  };

  const handleSearchAdd = () => {
    if (onAddPerson && searchQuery.trim()) {
      onAddPerson(searchQuery.trim());
    }
    setSearchQuery('');
    setSearchFocused(false);
    if (window.innerWidth <= 768) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Desktop hover zone - invisible trigger area on left edge */}
      {isDesktop && (
        <div
          ref={hoverZoneRef}
          className={`sidebar-hover-zone ${isOpen ? 'open' : ''}`}
          onMouseEnter={handleMouseEnter}
        />
      )}

      {/* Toggle button - mobile only */}
      {!isDesktop && (
        <button
          className={`sidebar-toggle-btn tour-sidebar-toggle ${isOpen ? 'open' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? 'Close sidebar' : 'Open sidebar'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      )}

      {/* Overlay to close sidebar when clicking outside - mobile only */}
      {!isDesktop && isOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar panel */}
      <div
        ref={sidebarRef}
        className={`sidebar ${isOpen ? 'open' : ''}`}
        onMouseEnter={isDesktop ? handleMouseEnter : undefined}
        onMouseLeave={isDesktop ? handleMouseLeave : undefined}
      >
        <div className="sidebar-content">
          {/* Close button for mobile */}
          <button
            className="sidebar-close-btn"
            onClick={() => setIsOpen(false)}
            aria-label="Close menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          {/* Main navigation */}
          <div className="sidebar-section">
            {/* Search/Add bar */}
            <div className="sidebar-search-container">
              <div className="sidebar-search-input-wrapper">
                <svg className="sidebar-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="sidebar-search-input"
                  placeholder="Add or find someone"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchQuery.trim()) {
                      if (filteredDonors.length > 0) {
                        handleSearchSelect(filteredDonors[0]);
                      } else {
                        handleSearchAdd();
                      }
                    }
                    if (e.key === 'Escape') {
                      setSearchQuery('');
                      searchInputRef.current?.blur();
                    }
                  }}
                />
                {searchQuery && (
                  <button
                    className="sidebar-search-clear"
                    onClick={() => {
                      setSearchQuery('');
                      searchInputRef.current?.focus();
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
                <button
                  className="sidebar-search-submit"
                  onClick={() => {
                    if (searchQuery.trim()) {
                      if (filteredDonors.length > 0) {
                        handleSearchSelect(filteredDonors[0]);
                      } else {
                        handleSearchAdd();
                      }
                    }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                    <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              {/* Search Results Dropdown */}
              {searchFocused && searchQuery.trim() && (
                <div className="sidebar-search-dropdown">
                  {filteredDonors.map((donor) => (
                    <button
                      key={donor.name}
                      className="sidebar-search-result"
                      onMouseDown={() => handleSearchSelect(donor)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      {donor.name}
                    </button>
                  ))}
                  {showAddOption && (
                    <button
                      className="sidebar-search-result sidebar-search-add"
                      onMouseDown={handleSearchAdd}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      Add "{searchQuery.trim()}"
                    </button>
                  )}
                  {filteredDonors.length === 0 && !showAddOption && (
                    <div className="sidebar-search-empty">No matches found</div>
                  )}
                </div>
              )}
            </div>

            {/* Chat */}
            <button
              className={`sidebar-nav-item tour-nav-ask ${view === 'ask' ? 'active' : ''}`}
              onClick={() => handleNavClick('ask')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Chat
            </button>

            {/* People - clicking shows Everyone */}
            <div className="sidebar-people-wrapper">
              <button
                className={`sidebar-nav-item sidebar-people-label ${view === 'allPeople' ? 'active' : ''} ${groupsSectionExpanded ? 'expanded' : ''}`}
                onClick={() => {
                  if (onSelectGroup) onSelectGroup(null);
                  if (onSelectList) onSelectList(null);
                  handleNavClick('allPeople');
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                People
              </button>
              <button
                className="sidebar-add-group-btn"
                onClick={handleCreateGroupClick}
                title="New group"
              >
                +
              </button>
            </div>

            {/* Groups list - collapsible */}
            <div className={`sidebar-groups-list ${groupsSectionExpanded ? 'expanded' : 'collapsed'}`}>
              {groups.map((group, index) => {
                // Get lists that belong to this group
                const groupLists = savedLists.filter(list => list.groupId === group.id);
                const isExpanded = expandedGroupId === group.id;
                const hasLists = groupLists.length > 0;
                return (
                  <div key={group.id} className="sidebar-group-container" style={{ '--item-index': index + 1 }}>
                    <div className="sidebar-group-item-wrapper">
                      <button
                        className={`sidebar-group-item ${view === 'people' && activeGroupId === group.id && !activeListId ? 'active' : ''} ${hasLists ? 'has-lists' : ''} ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => {
                          handleGroupSelect(group.id);
                          if (onSelectList) onSelectList(null); // Clear list selection when clicking group
                        }}
                      >
                        <span className="sidebar-group-name">{group.name}</span>
                      </button>
                      <button
                        className="sidebar-group-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmGroup(group);
                        }}
                        title="Delete group"
                      >
                        −
                      </button>
                    </div>
                    {/* Nested lists under this group */}
                    {hasLists && (
                      <div className={`sidebar-group-lists ${isExpanded ? 'expanded' : 'collapsed'}`}>
                        {groupLists.map((list) => (
                          <div key={list.id} className="sidebar-list-item-wrapper">
                            <button
                              className={`sidebar-group-list-item ${activeListId === list.id ? 'active' : ''}`}
                              onClick={() => handleListSelect(list.id, group.id)}
                            >
                              {list.name}
                            </button>
                            <button
                              className="sidebar-list-delete-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirmList(list);
                              }}
                              title="Delete list"
                            >
                              −
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Add Group button - visible on mobile */}
              <button
                className="sidebar-add-group-mobile"
                onClick={handleCreateGroupClick}
              >
                + Add Group
              </button>
            </div>
          </div>

          {/* Settings at the bottom */}
          <div className="sidebar-settings">
            <button
              className={`sidebar-settings-btn ${showSettings ? 'active' : ''}`}
              onClick={() => {
                if (onShowSettings) onShowSettings(!showSettings);
                if (window.innerWidth <= 768) {
                  setIsOpen(false);
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              Settings
            </button>
          </div>

        </div>
      </div>

      {/* Delete List Confirmation Modal */}
      {deleteConfirmList && (
        <div className="sidebar-delete-overlay" onClick={() => setDeleteConfirmList(null)}>
          <div className="sidebar-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete List</h3>
            <p>Are you sure you want to delete <strong>{deleteConfirmList.name}</strong>?</p>
            <div className="sidebar-delete-actions">
              <button
                className="sidebar-delete-cancel"
                onClick={() => setDeleteConfirmList(null)}
              >
                Cancel
              </button>
              <button
                className="sidebar-delete-confirm"
                onClick={() => {
                  if (onDeleteList) onDeleteList(deleteConfirmList.id);
                  setDeleteConfirmList(null);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Group Confirmation Modal */}
      {deleteConfirmGroup && (
        <div className="sidebar-delete-overlay" onClick={() => setDeleteConfirmGroup(null)}>
          <div className="sidebar-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Group</h3>
            <p>Are you sure you want to delete <strong>{deleteConfirmGroup.name}</strong>?</p>
            <p className="sidebar-delete-warning">This will also delete all people in this group.</p>
            <div className="sidebar-delete-actions">
              <button
                className="sidebar-delete-cancel"
                onClick={() => setDeleteConfirmGroup(null)}
              >
                Cancel
              </button>
              <button
                className="sidebar-delete-confirm"
                onClick={() => {
                  if (onDeleteGroup) onDeleteGroup(deleteConfirmGroup.id);
                  setDeleteConfirmGroup(null);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sidebar;
